/// <reference types="vite/client" />
import styles from './dealer-locator.css?inline';
import { loadBootstrapConfig, fetchDealersForLocator } from '../api/client';
import type { BootstrapConfig, DealerSummary } from '../api/types';

(function () {
  let script = document.currentScript as HTMLScriptElement | null;
  if (!script) {
    script = Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="module"]'))
      .find(s => s.src.includes('dealer-locator')) as HTMLScriptElement | undefined ?? null;
  }
  if (!script) return;

  const apiUrl = (script.dataset.apiUrl ?? '').replace(/\/+$/, '');
  if (apiUrl) {
    (window as any).__VC_API_URL__ = apiUrl;
  }

  const googleMapsApiKey = (script.dataset.googleMapsApiKey ?? '').trim();
  const explicitTenantId = (script.dataset.tenantId ?? '').trim();

  const containerId = script.dataset.container || '#vc-dealer-locator';
  let container = document.querySelector(containerId) as HTMLElement | null;
  if (!container) {
    container = document.createElement('div');
    container.id = containerId.replace(/^[#.]/, '');
    script.parentElement?.insertBefore(container, script);
  }

  // Clear any stale tenant id, then optionally set an explicit one for dev/test pages.
  localStorage.removeItem('vc_tenant_id');
  if (explicitTenantId && /^\d+$/.test(explicitTenantId)) {
    localStorage.setItem('vc_tenant_id', explicitTenantId);
  }

  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  document.head.appendChild(styleEl);

  container.classList.add('vc-dealer-locator');
  container.innerHTML = `
    <div class="vc-dealer-locator-body">
      <div class="vc-dealer-locator-map-wrap">
        <div id="vc-dealer-map" class="vc-dealer-locator-map"></div>
        <div id="vc-dealer-map-msg" class="vc-dealer-locator-map-msg"></div>
      </div>
      <div class="vc-dealer-locator-list-wrap">
        <div class="vc-dealer-locator-search">
          <div class="vc-dealer-locator-search-field">
            <label for="vc-dealer-zip">Find a dealer near you</label>
            <input type="text" id="vc-dealer-zip" class="vc-dealer-locator-input" placeholder="Enter ZIP / Postal Code" maxlength="10" />
          </div>
          <button type="button" id="vc-dealer-search-btn" class="vc-dealer-locator-btn">Search</button>
        </div>
        <div id="vc-dealer-list" class="vc-dealer-locator-list"></div>
      </div>
    </div>
  `;

  let map: any = null;
  let markers: any[] = [];
  let dealers: DealerSummary[] = [];
  let infoWindow: any = null;
  let googleMapsPromise: Promise<void> | null = null;

  function loadGoogleMaps(apiKey: string): Promise<void> {
    if (googleMapsPromise) return googleMapsPromise;
    if ((window as any).google?.maps) return Promise.resolve();

    googleMapsPromise = new Promise((resolve, reject) => {
      const callbackName = 'vcDealerLocatorMapInit' + Date.now();
      (window as any)[callbackName] = () => {
        resolve();
        delete (window as any)[callbackName];
      };

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}&loading=async`;
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('Failed to load Google Maps API'));
      document.head.appendChild(script);
    });

    return googleMapsPromise;
  }

  async function geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
    const geocoder = new (window as any).google.maps.Geocoder();
    try {
      const result = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
        geocoder.geocode({ address }, (results: any, status: any) => {
          if (status === 'OK' && results && results[0]) {
            const loc = results[0].geometry.location;
            resolve({ lat: loc.lat(), lng: loc.lng() });
          } else {
            resolve(null);
          }
        });
      });
      return result;
    } catch {
      return null;
    }
  }

  function ensureUrl(url: string): string {
    if (!url) return '#';
    // Reject dangerous schemes; only allow http/https.
    if (/^(javascript|data|vbscript):/i.test(url.trim())) return '#';
    if (!/^https?:\/\//i.test(url)) return 'https://' + url;
    return url;
  }

  function escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  const mapEl = container.querySelector('#vc-dealer-map') as HTMLElement;
  const listEl = container.querySelector('#vc-dealer-list') as HTMLElement;
  const zipInput = container.querySelector('#vc-dealer-zip') as HTMLInputElement;
  const searchBtn = container.querySelector('#vc-dealer-search-btn') as HTMLButtonElement;
  const mapMsgEl = container.querySelector('#vc-dealer-map-msg') as HTMLElement;

  async function init(): Promise<void> {
    if (!googleMapsApiKey) {
      renderError('Please add a Google Maps API key to the embed code (data-google-maps-api-key).');
      return;
    }

    container!.classList.add('vc-dealer-locator--loading');

    try {
      const [config] = await Promise.all([
        loadBootstrapConfig(),
        loadGoogleMaps(googleMapsApiKey),
      ]);

      applyTheme(container!, config);
      initMap();
      await loadDealers();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[vc-dealer-locator] init failed:', err);
      renderError('Unable to load the dealer locator. Please try again later.');
    } finally {
      container!.classList.remove('vc-dealer-locator--loading');
    }
  }

  function renderError(message: string): void {
    container!.innerHTML = `<p class="vc-dealer-locator-empty">${escapeHtml(message)}</p>`;
  }

  function applyTheme(el: HTMLElement, config: BootstrapConfig): void {
    const s = config.settings ?? {};
    const hex = (key: string, fallback: string): string =>
      typeof s[key] === 'string' && /^#[0-9a-fA-F]{6}$/.test(s[key] as string)
        ? (s[key] as string)
        : fallback;

    el.style.setProperty('--vc-primary', hex('themeColorPrimary', '#0e8e92'));
    el.style.setProperty('--vc-primary-hover', hex('themeColorPrimaryHover', '#1557b0'));
    el.style.setProperty('--vc-primary-light', hex('themeColorPrimaryLight', '#e8f0fe'));
    el.style.setProperty('--vc-text', hex('themeColorText', '#202124'));
    el.style.setProperty('--vc-bg', hex('themeColorBg', '#ffffff'));
    el.style.setProperty('--vc-border-solid', hex('themeColorBorder', '#0e8e92'));
    el.style.setProperty('--vc-border', hex('themeColorBorder', '#0e8e92') + '55');
  }

  function initMap(): void {
    map = new (window as any).google.maps.Map(mapEl, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });
    infoWindow = new (window as any).google.maps.InfoWindow();
  }

  async function loadDealers(postalCode = ''): Promise<void> {
    listEl.innerHTML = '<p class="vc-dealer-locator-loading">Loading dealers…</p>';
    try {
      dealers = await fetchDealersForLocator(postalCode);
      renderList(dealers);
      await renderMarkers(dealers);
    } catch {
      listEl.innerHTML = '<p class="vc-dealer-locator-empty">Unable to load dealers. Please try again.</p>';
    }
  }

  function renderList(items: DealerSummary[]): void {
    if (items.length === 0) {
      listEl.innerHTML = '<p class="vc-dealer-locator-empty">No dealers found.</p>';
      return;
    }

    listEl.innerHTML = items.map((d, index) => {
      const location = [d.address, d.city, d.state_province, d.postal_code].filter(Boolean).join(', ');
      const phone = d.phone ? `<a href="tel:${escapeHtml(d.phone.replace(/\s/g, ''))}">${escapeHtml(d.phone)}</a>` : '';
      const website = d.website ? `<a href="${ensureUrl(d.website)}" target="_blank" rel="noopener noreferrer">Website</a>` : '';
      const email = d.contact_email ? `<a href="mailto:${escapeHtml(d.contact_email)}">${escapeHtml(d.contact_email)}</a>` : '';
      return `
        <div class="vc-dealer-locator-card" data-dealer-index="${index}">
          <h4>${escapeHtml(d.dealer_name)}</h4>
          ${location ? `<p class="vc-dealer-locator-location">${escapeHtml(location)}</p>` : ''}
          <div class="vc-dealer-locator-links">
            ${phone}${phone && website ? ' · ' : ''}${website}
          </div>
          ${email ? `<div class="vc-dealer-locator-email">${email}</div>` : ''}
          <button type="button" class="vc-dealer-locator-card-btn" data-dealer-index="${index}">Show on map</button>
        </div>
      `;
    }).join('');

    listEl.querySelectorAll<HTMLButtonElement>('.vc-dealer-locator-card-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const index = parseInt(btn.dataset.dealerIndex ?? '-1', 10);
        focusDealer(index);
      });
    });
  }

  async function renderMarkers(items: DealerSummary[]): Promise<void> {
    if (!map) return;

    markers.forEach(m => m.setMap(null));
    markers = [];

    if (items.length === 0) return;

    const bounds = new (window as any).google.maps.LatLngBounds();
    let hasCoordinates = false;

    for (let i = 0; i < items.length; i++) {
      const d = items[i];
      let position: { lat: number; lng: number } | null = null;

      if (d.latitude != null && d.longitude != null) {
        position = { lat: d.latitude, lng: d.longitude };
      } else if (d.address || d.city || d.postal_code) {
        const address = [d.address, d.city, d.state_province, d.postal_code, d.country].filter(Boolean).join(', ');
        position = await geocodeAddress(address);
      }

      if (!position) continue;

      hasCoordinates = true;
      const marker = new (window as any).google.maps.Marker({
        position,
        map,
        title: d.dealer_name,
      });

      marker.addListener('click', () => {
        openInfoWindow(d, marker);
        highlightCard(i);
      });

      markers.push(marker);
      bounds.extend(position);
    }

    if (hasCoordinates) {
      map.fitBounds(bounds);
      // Don't zoom in too far for a single result
      const listener = (window as any).google.maps.event.addListener(map, 'idle', () => {
        if (map!.getZoom()! > 15) map!.setZoom(15);
        (window as any).google.maps.event.removeListener(listener);
      });
    } else {
      mapMsgEl.textContent = 'No map coordinates available for these dealers.';
    }
  }

  function openInfoWindow(d: DealerSummary, marker: any): void {
    if (!infoWindow) return;
    const location = [d.address, d.city, d.state_province, d.postal_code].filter(Boolean).join(', ');
    const phone = d.phone ? `<div><a href="tel:${escapeHtml(d.phone.replace(/\s/g, ''))}">${escapeHtml(d.phone)}</a></div>` : '';
    const website = d.website ? `<div><a href="${ensureUrl(d.website)}" target="_blank" rel="noopener noreferrer">Website</a></div>` : '';
    infoWindow.setContent(`
      <div class="vc-dealer-locator-info-window">
        <strong>${escapeHtml(d.dealer_name)}</strong>
        ${location ? `<div>${escapeHtml(location)}</div>` : ''}
        ${phone}${website}
      </div>
    `);
    infoWindow.open(map, marker);
  }

  function focusDealer(index: number): void {
    const marker = markers[index];
    const dealer = dealers[index];
    if (marker && dealer && map) {
      map.setCenter(marker.getPosition()!);
      map.setZoom(14);
      openInfoWindow(dealer, marker);
      highlightCard(index);
    }
  }

  function highlightCard(index: number): void {
    listEl.querySelectorAll<HTMLElement>('.vc-dealer-locator-card').forEach((card, i) => {
      card.classList.toggle('vc-dealer-locator-card--active', i === index);
    });
    const active = listEl.querySelector<HTMLElement>(`.vc-dealer-locator-card[data-dealer-index="${index}"]`);
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  async function handleSearch(): Promise<void> {
    const zip = zipInput.value.trim();
    searchBtn.disabled = true;
    searchBtn.textContent = 'Searching…';
    await loadDealers(zip);

    if (zip && map) {
      const center = await geocodeAddress(zip);
      if (center) {
        map.setCenter(center);
        map.setZoom(10);
      }
    }

    searchBtn.disabled = false;
    searchBtn.textContent = 'Search';
  }

  searchBtn.addEventListener('click', () => { void handleSearch(); });
  zipInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void handleSearch();
  });

  void init();
})();
